/**
 * Story 24.7 — phone navigation rules (DESIGN.md › BottomTabBar,
 * EXPERIENCE.md › IA › Phone and tablet).
 *
 * Pure and dependency-free: no React, no `window` at import time, so the
 * Playwright suites may import it as well as the components.
 */

export type PhoneTab = 'chats' | 'tasks' | 'boards' | 'more';

/** Where each tab goes. `/dashboards` keeps its route and is labelled "Boards". */
export const PHONE_TAB_ROOTS: Readonly<Record<PhoneTab, string>> = {
  chats: '/chat',
  tasks: '/tasks',
  boards: '/dashboards',
  more: '/more',
};

function isUnder(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`);
}

/**
 * The tab that owns `pathname`. Chats, Tasks and Boards own their root and
 * every route under it; **More is active for every other route** — Calendar,
 * Library, Your team, Documentation, Settings, Engineering and anything
 * reached through them, as well as `/more` itself.
 */
export function activeTabFor(pathname: string): PhoneTab {
  if (isUnder(pathname, PHONE_TAB_ROOTS.chats)) return 'chats';
  if (isUnder(pathname, PHONE_TAB_ROOTS.tasks)) return 'tasks';
  if (isUnder(pathname, PHONE_TAB_ROOTS.boards)) return 'boards';
  return 'more';
}

/** The `/chat` URL params that open a thread (`useChatUrlState`). */
const THREAD_PARAMS = ['agent', 'advisoryId', 'taskId'] as const;

/**
 * 🔴 THE ONE hide path for the tab bar. The bar is hidden (and `inert`) while
 * the on-screen keyboard is open, and inside a chat thread: `/chat` with a
 * non-empty `agent`, `advisoryId` or `taskId`, or `new=task`. Story 24.7b
 * supplies `keyboardOpen`; later chat stories reuse this function rather than
 * adding a second rule.
 */
export function isTabBarHidden(
  location: { pathname: string; search: string },
  keyboardOpen = false,
): boolean {
  if (keyboardOpen) return true;
  // Trailing slashes normalised: React Router matches `/chat/` as `/chat`.
  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  if (pathname !== PHONE_TAB_ROOTS.chats) return false;
  const params = new URLSearchParams(location.search);
  if (THREAD_PARAMS.some((name) => (params.get(name) ?? '') !== '')) return true;
  return params.get('new') === 'task';
}
